import {
  IconButton,
  InfoIcon,
  Scrollbar,
  Text,
  toast,
  type DataTableColumn,
} from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  runStorageCleanup,
  type StorageCleanupPreview,
  type StorageCleanupRequest,
} from "@/api/adminStorage";
import { ResourceBrowserLayout, PaginationFooter } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { HatTextModal } from "@/components/modal";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { hatcheryTextModes } from "@/app/theme";
import { HatDataTable } from "@/components/table/HatDataTable";
import { useAdminTable } from "@/app/useAdminTable";
import { parsePageParam } from "@/utils";
import { adminOperationError } from "@/app/adminOperationError";
import { formatBytes } from "@/utils";
import { CleanupTargetSource, type CleanupTargetRow } from "./StorageRecoveryTableSource";
import styles from "./AdminStoragePage.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

type PreviewState = {
  source: CleanupTargetSource;
  request: StorageCleanupRequest;
  result: StorageCleanupPreview;
};

export function StorageCleanupPanel() {
  const [params] = useSearchParams();
  const page = parsePageParam(params.get("page"));
  const rawStatus = params.get("status");
  const status = rawStatus === "archived" || rawStatus === "cancelled" ? rawStatus : undefined;
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [receipt, setReceipt] = useState<StorageCleanupPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const selectionVersion = useRef(0);
  const source = useMemo(
    () =>
      new CleanupTargetSource(status, page, () => {
        selectionVersion.current += 1;
        setPreview(null);
      }),
    [status, page],
  );
  const currentSource = useRef(source);
  useEffect(() => {
    currentSource.current = source;
  }, [source]);
  // 선택 범위를 확인하는 동안 대상이 바뀌지 않도록 이 목록은 명시적으로 새로고침한다.
  const snapshot = useAdminTable(source, false);
  const { setPage } = snapshot;
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const currentPreview = preview?.source === source ? preview : null;
  const loadPreview = async () => {
    if (busy || snapshot.selectedIds.size === 0) return;
    const request: StorageCleanupRequest = {
      dataset_uuids: [...snapshot.selectedIds],
      ...(status ? { statuses: [status] } : {}),
    };
    const version = selectionVersion.current;
    setBusy(true);
    setReceipt(null);
    try {
      const result = await runStorageCleanup("preview", request);
      if (alive.current && currentSource.current === source && selectionVersion.current === version)
        setPreview({ source, request, result });
    } catch (error) {
      if (alive.current) toast.error(adminOperationError(error));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const execute = async () => {
    if (
      busy ||
      currentPreview === null ||
      !currentPreview.result.entries.some((entry) => entry.eligible)
    )
      return;
    setBusy(true);
    try {
      const result = await runStorageCleanup("execute", currentPreview.request);
      if (alive.current) {
        setReceipt(result);
        toast.success("회수 요청 결과를 받았습니다.");
      }
    } catch (error) {
      if (alive.current) toast.error(adminOperationError(error));
    } finally {
      if (alive.current) {
        setBusy(false);
        setPreview(null);
        currentSource.current.clearSelection();
        await currentSource.current.refresh();
      }
    }
  };
  const columns: readonly DataTableColumn<CleanupTargetRow>[] = [
    {
      key: "target",
      header: "Dataset / 경로",
      width: "minmax(320px, 1fr)",
      render: (row) => (
        <div className={styles.details}>
          <span>{row.target.display_name}</span>
          <span>{row.target.dataset_uuid}</span>
          <span>{row.target.object_key_prefix}</span>
        </div>
      ),
    },
    { key: "status", header: "상태", width: "130px", render: (row) => row.status },
    {
      key: "markers",
      header: "회수 판단",
      width: "260px",
      render: (row) => (
        <div className={styles.details}>
          <span>{row.target.eligible ? "실행 가능" : "실행 제외"}</span>
          {row.target.shared_anchor && <span>공유 anchor: 실행 제외</span>}
          {row.target.training_registered && <span>Training 등록됨: 회수 시 마운트 영향</span>}
          <span>{row.target.retention_elapsed ? "보존 기간 경과" : "보존 기간 이내"}</span>
        </div>
      ),
    },
    {
      key: "bytes",
      header: "회수 예상량",
      width: "150px",
      render: (row) => formatBytes(row.bytes),
    },
    {
      key: "job",
      header: "회수 작업",
      width: "240px",
      render: (row) => (
        <div className={styles.details}>
          <span>{row.job ?? "미접수"}</span>
          {row.target.job_error && <span>{JSON.stringify(row.target.job_error)}</span>}
        </div>
      ),
    },
  ];
  return (
    <div className={styles.sections}>
      <div className={styles.notice}>
        <span className={styles.noticeIcon} aria-hidden="true">
          <InfoIcon />
        </span>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.noticeText}>
          선택한 Dataset의 파일을 물리적으로 삭제합니다. 공유 anchor는 제외되며, Training에 등록된
          대상은 마운트에 영향을 줍니다. 미리보기와 실행 시 서버가 대상을 다시 판정합니다.
        </Text>
      </div>
      <ResourceBrowserLayout
        ariaLabel="스토리지 회수 대상"
        summaryCount={snapshot.total}
        summaryLabel="회수 대상"
        summaryInFilter
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
        footer={
          <PaginationFooter
            page={page}
            size={snapshot.size}
            total={snapshot.total}
            isLoading={busy || snapshot.loadState === "loading"}
            ariaLabel="회수 대상 페이지 이동"
            onPrevious={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        }
      >
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            ariaLabel="스토리지 회수 도구"
            source={source}
            idleContent={<FilterResultCount count={snapshot.total} unitLabel="회수 대상" />}
            actions={
              <IconButton
                text="선택 범위 미리보기"
                size="sm"
                variant="surface"
                disabled={busy || snapshot.selectedIds.size === 0}
                onClick={() => void loadPreview()}
              />
            }
            rightContent={
              <IconButton
                text="새로고침"
                size="sm"
                variant="surface"
                disabled={busy || snapshot.loadState === "loading"}
                onClick={() => {
                  setPreview(null);
                  void source.refresh();
                }}
              />
            }
          />
          <HatDataTable
            source={source}
            columns={columns}
            ariaLabel="스토리지 회수 테이블"
            showCheckbox
            emptyMessage="회수 대상이 없습니다."
            minWidth="1080px"
            scrollable
          />
        </div>
      </ResourceBrowserLayout>
      {receipt && (
        <section className={styles.receipt} aria-label="회수 실행 접수 결과">
          <p className={styles.receiptNote}>
            실행 응답입니다. queued·running은 삭제 완료가 아닙니다. 새로고침으로 대상의 최신 상태를
            확인하세요.
          </p>
          <Scrollbar className={styles.receiptScroll} gutter="auto">
            <CleanupResult result={receipt} />
          </Scrollbar>
        </section>
      )}
      <HatTextModal
        open={currentPreview !== null}
        label="스토리지 회수 미리보기"
        caption="동일한 선택 범위로 회수 작업을 접수합니다. Worker가 파일을 삭제하면 되돌릴 수 없습니다."
        content={currentPreview && <CleanupResult result={currentPreview.result} />}
        size="md"
        okText="회수 실행 접수"
        error
        busy={busy}
        okDisabled={!currentPreview?.result.entries.some((entry) => entry.eligible)}
        onOkPress={() => void execute()}
        onCancelPress={() => setPreview(null)}
      />
    </div>
  );
}

function CleanupResult({ result }: { result: StorageCleanupPreview }) {
  return (
    <div className={styles.details}>
      <p>
        서버 분류 {result.total}건 · 회수 예상 {formatBytes(result.freed_bytes)}
      </p>
      <ul>
        {result.entries.map((entry) => (
          <li key={entry.dataset_uuid}>
            {entry.display_name} ({entry.dataset_uuid}) ·{" "}
            {entry.eligible ? "실행 가능" : "실행 제외"} · {entry.job_status ?? "미접수"}
            {entry.training_registered ? " · Training 마운트 영향" : ""}
            {entry.shared_anchor ? " · 공유 anchor" : ""}
          </li>
        ))}
      </ul>
      {result.errors.length > 0 && (
        <ul aria-label="회수 제외 사유">
          {result.errors.map((error) => (
            <li key={error.dataset_uuid}>
              {error.dataset_uuid}: {error.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
